import { useState } from 'react'
import { Share2 } from 'lucide-react'
import {
  isCurrentUserAdminSelector,
  useGetCurrentUserQuery
} from '@axilon-apps/domains-identity/state'
import {
  useAppSelector,
  useGetResourceAccessQuery,
  type ResourceAccessLevel
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { canManageScope } from './access-levels'
import { ResourceSharingDialog } from './ResourceSharingDialog'

export interface ResourceShareButtonProps {
  resourceName: string
  resourceLabel: string
  accessScopeId?: string
  /** Optional resource-specific explanation shown in the sharing dialog. */
  scopeDescription?: string
  /** Access levels this resource supports. Defaults to Viewer, Editor, and Co-owner. */
  allowedAccessLevels?: readonly ResourceAccessLevel[]
  /** Use an icon-only control when sharing is embedded in a compact row. */
  compact?: boolean
  /**
   * Always-visible ghost icon matching page/rail header chrome. Unlike
   * `compact`, this does not wait for hover.
   */
  toolbar?: boolean
}

/**
 * Reusable Share affordance for any resource carrying an access scope.
 * It stays hidden unless the current user owns or co-owns that scope or is a
 * global admin; the proxy remains authoritative when grants are changed.
 */
export function ResourceShareButton({
  resourceName,
  resourceLabel,
  accessScopeId,
  scopeDescription,
  allowedAccessLevels,
  compact = false,
  toolbar = false
}: ResourceShareButtonProps) {
  const isAdmin = useAppSelector(isCurrentUserAdminSelector)
  const scopeId = accessScopeId ?? ''
  const scope = useGetResourceAccessQuery(scopeId, { skip: scopeId === '' })
  const currentUser = useGetCurrentUserQuery()
  const [open, setOpen] = useState(false)

  const canManage = isAdmin || canManageScope(scope.data, currentUser.data?.id)
  if (!canManage) return null

  const iconOnly = compact || toolbar
  const shareLabel = `Share ${resourceLabel} "${resourceName}"`
  const trigger = (
    <Button
      variant={toolbar || compact ? 'ghost' : 'outline'}
      size={toolbar ? 'icon-sm' : compact ? 'icon-xs' : 'sm'}
      className={compact && !toolbar ? 'opacity-0 group-hover:opacity-100' : undefined}
      aria-label={iconOnly ? shareLabel : undefined}
      onClick={(event) => {
        event.stopPropagation()
        setOpen(true)
      }}
    >
      <Share2 className={toolbar ? 'size-4' : undefined} size={toolbar ? undefined : 14} />
      {!iconOnly && 'Share'}
    </Button>
  )

  return (
    <>
      {toolbar ? (
        <TooltipProvider delayDuration={700}>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="bottom">Share</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <ResourceSharingDialog
        resourceName={resourceName}
        resourceLabel={resourceLabel}
        scopeId={scopeId}
        scopeDescription={scopeDescription}
        allowedAccessLevels={allowedAccessLevels}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  )
}
